import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { SketchPicker } from 'react-color';

import { colors } from 'src/global/styles/colors';
import useClickOutside from 'src/hooks/useClickOutside';

import { AdminNumberFormat } from './AdminNumberFormat';

interface ColorPickerProps {
  color: string;
  onColorChange: (color: string) => void;
  opacity: number;
  onOpacityChange: (opacity: number) => void;
}

// Helper function to convert hex to RGB
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

export function ColorPicker({ color, onColorChange, opacity, onOpacityChange }: ColorPickerProps) {
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const colorPickerRef = useRef<HTMLDivElement | null>(null);
  const boxColorRef = useRef<HTMLDivElement | null>(null);

  useClickOutside(boxColorRef, () => setDisplayColorPicker(false));

  // Convert hex color + opacity to RGB object with alpha for SketchPicker
  const colorWithOpacity = useMemo(() => {
    const rgbColor = hexToRgb(color);
    return rgbColor
      ? {
          r: rgbColor.r,
          g: rgbColor.g,
          b: rgbColor.b,
          a: opacity,
        }
      : color;
  }, [color, opacity]);

  const handleChangeColor = useCallback(
    (colorResult: any) => {
      // Extract hex color - always available in colorResult.hex
      const newHexColor = colorResult.hex || colorResult.hexString;

      // Only update color if it changed
      if (newHexColor && newHexColor !== color) {
        onColorChange(newHexColor);
      }

      // Extract opacity from RGB alpha channel
      const newOpacityValue = colorResult.rgb?.a;

      // Only update opacity if it's provided and different from current
      if (newOpacityValue !== undefined) {
        const newOpacityPercent = newOpacityValue * 100;
        // Only call if opacity actually changed (with small tolerance for floating point)
        if (Math.abs(newOpacityPercent - opacity * 100) > 0.01) {
          onOpacityChange(newOpacityPercent);
        }
      }
    },
    [color, opacity, onColorChange, onOpacityChange],
  );

  return (
    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} spacing={1}>
      <Box
        ref={boxColorRef}
        onClick={() => setDisplayColorPicker(true)}
        width={'70%'}
        padding={'12px'}
        border={`1px solid ${colors.neutral015}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
        }}>
        {displayColorPicker ? (
          <Box
            ref={colorPickerRef}
            sx={{
              position: 'absolute',
              zIndex: 2,
              bottom: 0,
            }}>
            <Box>
              <SketchPicker color={colorWithOpacity} onChange={handleChangeColor} />
            </Box>
          </Box>
        ) : null}
        <Box
          width={'28px'}
          height={'28px'}
          bgcolor={color}
          sx={{
            opacity: opacity,
          }}
        />
        <Typography marginLeft={'12px'} fontSize={'14px'} lineHeight={'20px'} sx={{ color: 'white' }}>
          {color}
        </Typography>
      </Box>
      <Box width={'30%'}>
        <AdminNumberFormat
          isAllowed={values => {
            const { floatValue } = values;
            if (!floatValue) return true;
            return floatValue >= 0 && floatValue <= 100;
          }}
          value={opacity * 100}
          onValueChange={e => {
            onOpacityChange(e.floatValue ? Number(e.floatValue) : 0);
          }}
          suffix='%'
        />
      </Box>
    </Stack>
  );
}
